"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Session-based auth against the backend's server-side OAuth flow (Keycloak).
 * The browser never sees a token — it holds an httpOnly session cookie, and
 * this context just asks the backend who the current user is.
 */
const AuthContext = createContext(null);

/** Sends the browser into the OAuth login flow (backend redirect to Keycloak). */
export function signIn() {
  window.location.href = "/api/auth/login";
}

/**
 * Ends the backend session (POST, so it rides on the SameSite cookie and
 * can't be triggered cross-site) and follows Keycloak's RP-initiated logout.
 */
export async function signOut() {
  const res = await fetch("/api/auth/logout", { method: "POST" });
  const { url } = await res.json();
  window.location.href = url;
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null });

  useEffect(() => {
    fetch("/api/auth/user")
      .then((r) => (r.ok ? r.json() : null))
      .then((user) => setState({ loading: false, user }))
      .catch(() => setState({ loading: false, user: null }));
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  const { user, loading } = ctx;
  const roles = user?.roles ?? [];
  // Role checks here are UX only (hide/show UI) — the backend's RolesGuard
  // is what actually enforces authorization.
  const hasRole = (role) => roles.includes(role);
  return {
    user,
    loading,
    roles,
    hasRole,
    isAdmin: hasRole("admin"),
    isOperator: hasRole("operator"),
    isScreen: hasRole("screen"),
    isTotem: hasRole("totem"),
  };
}
